import {
  type Download,
  type FetchData,
  type FetchDataWithError,
  useDataFetcher,
} from '@hooks/useDataFetcher';
import {
  createContext,
  type JSX,
  type PropsWithChildren,
  useContext,
} from 'react';
import type { AuthProps } from 'types/core';
import type {
  GenericManager,
  UseConfirmation,
  UseFinancingOffers,
  UseLayout,
  UseLocalization,
  UseMarkets,
  UseMasters,
  UseOffers,
  UseOffersV2,
  UsePartners,
  UsePopAuthz,
  UsePriceData,
  UseProductDetails,
  UseProducts,
  User,
  UseSnackbar,
} from './types';
import { type UseAssortment, useAssortment } from './useAssortment';
import { useAuth } from './useAuth';
import { useConfirmation } from './useConfirmation';
import { type UseFinances, useFinances } from './useFinances';
import { useFinancingOffers } from './useFinancingOffers';
import {
  type UseFinancingProducts,
  useFinancingProducts,
} from './useFinancingProducts';
import { useLayout } from './useLayout';
import { useLocalizations } from './useLocalizations';
import { useManager } from './useManage';
import { useMarkets } from './useMarkets';
import { useMasters } from './useMasters';
import { useOffers } from './useOffers';
import { useOffersV2 } from './useOffersV2';
import { usePartners } from './usePartners';
import { type UsePCF, usePCF } from './usePCF';
import { usePopAuthz } from './usePopAuthz';
import { usePriceData } from './usePriceData';
import { useProductDetails } from './useProductDetails';
import { useProducts } from './useProducts';
import { useSnackbar } from './useSnackbar';
import { type UseStores, useStores } from './useStores';

type StoreProviderProps = AuthProps &
  PropsWithChildren & {
    username?: string;
  };

type Store = {
  partners: ReturnType<UsePartners>;
  markets: ReturnType<UseMarkets>;
  loggedUser: User;
  layout: UseLayout;
  offers: UseOffers;
  popAuth: ReturnType<UsePopAuthz>;
  stores: UseStores;
  masters: UseMasters;
  finances: UseFinances;
  snackbar: UseSnackbar;
  priceData: ReturnType<UsePriceData>;
  confirmation: UseConfirmation;
  contentModels: GenericManager;
  localizations: ReturnType<UseLocalization>;
  offersV2: ReturnType<UseOffersV2>;
  products: ReturnType<UseProducts>;
  productDetails: UseProductDetails;
  financingOffers: UseFinancingOffers;
  financingProducts: UseFinancingProducts;
  pcf: ReturnType<UsePCF>;
  assortment: ReturnType<UseAssortment>;
  dataFetcher: {
    fetchData: FetchData;
    fetchDataWithError: FetchDataWithError;
    download: Download;
  };
};

const StoreContext = createContext<Store>(undefined!);

export const StoreProvider = ({
  login,
  children,
}: StoreProviderProps): JSX.Element => {
  const snackbar = useSnackbar();
  const [, snackbarActions] = snackbar;
  const dataFetcher = useDataFetcher(login, snackbarActions);
  const offers = useOffers(
    dataFetcher.fetchDataWithError,
    dataFetcher.fetchData,
    dataFetcher.download,
  );
  const offersV2 = useOffersV2(dataFetcher.fetchData, snackbarActions);
  const layout = useLayout();
  const confirmation = useConfirmation();
  const partners = usePartners(dataFetcher.fetchData, snackbarActions);
  const stores = useStores(dataFetcher.fetchData);
  const loggedUser = useAuth(dataFetcher.fetchData);
  const markets = useMarkets(dataFetcher.fetchData, snackbarActions);
  const masters = useMasters(dataFetcher.fetchData);
  const popAuth = usePopAuthz(dataFetcher.fetchData, snackbarActions);
  const finances = useFinances(dataFetcher.fetchData);
  const priceData = usePriceData(dataFetcher.fetchData, snackbarActions);
  const contentModels = useManager(dataFetcher.fetchData);
  const products = useProducts(dataFetcher.fetchData, snackbarActions);
  const localizations = useLocalizations(
    dataFetcher.fetchData,
    snackbarActions,
  );
  const productDetails = useProductDetails(dataFetcher.fetchData);
  const pcf = usePCF(dataFetcher.fetchData, snackbarActions);
  const assortment = useAssortment(dataFetcher.fetchData, snackbarActions);
  const financingOffers = useFinancingOffers(
    dataFetcher.fetchData,
    snackbarActions,
  );
  const financingProducts = useFinancingProducts(
    dataFetcher.fetchData,
    snackbarActions,
  );

  return (
    <StoreContext.Provider
      value={{
        pcf,
        offers,
        layout,
        stores,
        popAuth,
        markets,
        masters,
        finances,
        offersV2,
        products,
        snackbar,
        priceData,
        assortment,
        partners,
        loggedUser,
        dataFetcher,
        confirmation,
        contentModels,
        localizations,
        productDetails,
        financingOffers,
        financingProducts,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => useContext(StoreContext);
